import { Box, TableCell, Typography } from '@mui/material';
import Image from 'next/image';
import { useMemo } from 'react';
import { CtaLink } from '@repo/segment/components/CtaLink';
import { LinkType } from '@repo/segment/enums';
import type { AnalyticsType } from '@repo/segment/types';
import { EntityPlacementCtaTypeEnum } from '@repo/supergraph/marketplace/enums';
import { CtaButton } from '@repo/ui/CtaButton';
import { ShortHorizontalLayout } from '@repo/ui/StarRating/ShortHorizontalLayout';
import { stripFederallyInsuredAnnotation } from '@repo/ui/utils/cta-utils';
import type { SummaryTableRowWithDrivers } from '../../types';
import { DEFAULT_RATING_TOOLTIP } from '../../utils';

interface ProductCellProps {
  beforeMainContent?: React.ReactNode;
  analyticsPayload: AnalyticsType;
  marketplaceEntity: SummaryTableRowWithDrivers['marketplaceEntity'];
}

const ProductImage = ({ source, alt }: { source: string; alt: string }) => (
  <Image src={source} alt={alt} width={122} height={44} style={{ objectFit: 'contain' }} />
);

export const ProductCell = ({
  beforeMainContent,
  analyticsPayload,
  marketplaceEntity,
}: ProductCellProps) => {
  const {
    displayName,
    name,
    image,
    starRating,
    reviewLink,
    federallyInsuredAnnotation,
    starRatingTooltip,
    ctaOverride: precomputedCtaOverride,
    cta: originalCta,
  } = marketplaceEntity;

  const ctaOverride = useMemo(() => {
    return stripFederallyInsuredAnnotation(
      precomputedCtaOverride ?? originalCta,
      federallyInsuredAnnotation,
    );
  }, [precomputedCtaOverride, originalCta, federallyInsuredAnnotation]);

  return (
    <TableCell
      sx={{
        bgcolor: 'neutral.lightest',
        padding: 0,
        borderRight: '1px solid',
        borderColor: 'neutral.lighter2',
        paddingTop: 'var(--cell-padding-top)',
      }}
    >
      {beforeMainContent}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          paddingX: 2,
          paddingY: 3,
          gap: 2,
        }}
      >
        {image?.source &&
          (ctaOverride ? (
            <CtaLink
              Component={Box}
              cta={{ ...ctaOverride, type: EntityPlacementCtaTypeEnum.ProductImage }}
              monetizable={analyticsPayload.monetizable}
              analyticsPayload={analyticsPayload}
              linkType={LinkType.PRODUCT_IMAGE}
            >
              <ProductImage
                source={image.source}
                alt={image.alt || displayName || 'Product logo'}
              />
            </CtaLink>
          ) : (
            <ProductImage source={image.source} alt={image.alt || displayName || 'Product logo'} />
          ))}
        <Typography sx={{ textAlign: 'center', fontSize: '14px', fontWeight: 325 }}>
          {displayName || name}
        </Typography>
        <ShortHorizontalLayout
          starRating={starRating}
          reviewLink={reviewLink}
          starRatingTooltip={starRatingTooltip ?? DEFAULT_RATING_TOOLTIP}
        />
        {ctaOverride && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              width: '100%',
            }}
          >
            <CtaButton
              cta={ctaOverride}
              analyticsPayload={analyticsPayload}
              sx={{ height: 45 }}
              hideSubtext
              fullWidth
            />
            <Typography
              variant="textExtraSmall"
              sx={{ textAlign: 'center', color: 'neutral.dark' }}
            >
              {ctaOverride.subtext}
            </Typography>
          </Box>
        )}
        {ctaOverride?.subtext && federallyInsuredAnnotation && (
          <Box
            sx={{
              bgcolor: 'yellows.lighter2',
              borderRadius: 1,
              width: '100%',
              textAlign: 'center',
              paddingX: 3,
            }}
          >
            <Typography variant="textExtraSmall" sx={{ color: 'neutral.darker', fontWeight: 500 }}>
              {' '}
              {federallyInsuredAnnotation}
            </Typography>
          </Box>
        )}
      </Box>
    </TableCell>
  );
};
